import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { Sparkles, X } from 'lucide-react';
import { getMyAiCredits } from '../../services/api/implementation/impl';
import {
  AI_CREDITS_CHANGED_EVENT,
  AI_CREDITS_EXHAUSTED_EVENT
} from '../../services/api/implementation/axiosInstance';

const INTEGRATION_PATH = '/builder/integration?openAi=1';
const BANNER_DISMISS_KEY = 'aiCreditsExhaustedBannerDismissed';
const SEEN_PREFIX = 'aiUsageMilestoneSeen';
const EXHAUSTED_FALLBACK =
  'Your free Housingram AI tokens are used up. Add your own AI API key under Integrations → AI Integration to keep using AI features.';

function storageGet(store, key) {
  try {
    return window[store].getItem(key);
  } catch {
    return null;
  }
}

function storageSet(store, key, value) {
  try {
    if (value == null) window[store].removeItem(key);
    else window[store].setItem(key, value);
  } catch {
    // storage unavailable — notices may repeat, which is harmless
  }
}

function monthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Which usage pop-up is due, if any. Builder milestones are once per builder
 * (the free tokens don't reset); a user's own cap resets monthly, so its
 * milestones are once per month.
 */
function pendingPopup(data) {
  const credits = data?.credits;
  const builderId = data?.builderId;
  if (!builderId || !credits?.enforcementEnabled) return null;

  const builderStep = Number(credits.usageMilestone) || 0;
  if (builderStep > 0 && builderStep < 100) {
    const key = `${SEEN_PREFIX}:builder:${builderId}`;
    const seen = Number(storageGet('localStorage', key)) || 0;
    if (builderStep > seen) {
      return {
        key,
        step: builderStep,
        title: `${credits.usedPercent}% of your free AI tokens used`,
        message: credits.userMessage,
        used: credits.freeTokensUsed,
        total: credits.freeTokensTotal,
        percent: credits.usedPercent
      };
    }
  }

  const userLimit = data?.userLimit;
  const userStep = Number(userLimit?.usageMilestone) || 0;
  if (userStep > 0 && !data?.provider?.useOwnKey) {
    const key = `${SEEN_PREFIX}:user:${builderId}:${monthKey()}`;
    const seen = Number(storageGet('localStorage', key)) || 0;
    if (userStep > seen) {
      return {
        key,
        step: userStep,
        title:
          userStep >= 100
            ? 'Your monthly AI limit is reached'
            : `${userLimit.usedPercent}% of your monthly AI limit used`,
        message: userLimit.userMessage,
        used: userLimit.tokensUsedThisMonth,
        total: userLimit.monthlyTokenCap,
        percent: userLimit.usedPercent
      };
    }
  }
  return null;
}

function UsageBar({ percent }) {
  const p = Math.max(0, Math.min(100, Number(percent) || 0));
  const color = p >= 90 ? 'bg-red-500' : p >= 75 ? 'bg-amber-500' : 'bg-teal-600';
  return (
    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
      <div className={`h-full ${color}`} style={{ width: `${p}%` }} />
    </div>
  );
}

function OwnerAction({ canManage }) {
  return canManage ? (
    <a
      href={INTEGRATION_PATH}
      className="inline-flex items-center rounded-lg bg-[#1E4A53] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#163a42]">
      Add your API key
    </a>
  ) : (
    <span className="text-xs text-gray-600">Ask your builder account owner to add an AI API key.</span>
  );
}

/**
 * Account-wide AI usage notices, mounted at the app shell (outside the router)
 * so they show on every builder page, not only on the AI cards:
 *  - a one-time pop-up at 50 / 75 / 90% of the builder's free tokens, and at
 *    50 / 75 / 90 / 100% of a user's own monthly cap;
 *  - a top banner once the free tokens are used up (100%).
 * Super-admins and users without a builder get `credits: null` from /me and
 * see nothing; own-key builders get milestone 0 from the server.
 */
export default function AiUsageNotices() {
  const isAuthenticated = Boolean(Cookies.get('token'));
  const queryClient = useQueryClient();
  const [bannerDismissed, setBannerDismissed] = useState(
    () => storageGet('sessionStorage', BANNER_DISMISS_KEY) === '1'
  );
  const [, forceRender] = useState(0);
  const lastRefresh = useRef(0);

  const { data } = useQuery({
    queryKey: ['myAiCredits'],
    queryFn: getMyAiCredits,
    enabled: isAuthenticated,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: false
  });

  useEffect(() => {
    const refresh = () => {
      // AI responses can arrive in bursts; one refetch per 20s is plenty.
      if (Date.now() - lastRefresh.current < 20_000) return;
      lastRefresh.current = Date.now();
      queryClient.invalidateQueries({ queryKey: ['myAiCredits'] });
    };
    const onExhausted = () => {
      storageSet('sessionStorage', BANNER_DISMISS_KEY, null);
      setBannerDismissed(false);
      lastRefresh.current = 0;
      refresh();
    };
    window.addEventListener(AI_CREDITS_CHANGED_EVENT, refresh);
    window.addEventListener(AI_CREDITS_EXHAUSTED_EVENT, onExhausted);
    return () => {
      window.removeEventListener(AI_CREDITS_CHANGED_EVENT, refresh);
      window.removeEventListener(AI_CREDITS_EXHAUSTED_EVENT, onExhausted);
    };
  }, [queryClient]);

  if (!isAuthenticated || !data?.credits) return null;

  const credits = data.credits;
  const exhausted =
    credits.enforcementEnabled && credits.depleted && !data.provider?.useOwnKey;
  const popup = pendingPopup(data);

  const closePopup = () => {
    storageSet('localStorage', popup.key, String(popup.step));
    forceRender((n) => n + 1);
  };

  return (
    <>
      {exhausted && !bannerDismissed ? (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[1300] flex justify-center px-4 pt-2">
          <div className="pointer-events-auto flex w-full max-w-3xl items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-lg">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
            <div className="flex-1">
              <p className="leading-relaxed">{credits.userMessage || EXHAUSTED_FALLBACK}</p>
              <div className="mt-2">
                <OwnerAction canManage={data.canManage} />
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                storageSet('sessionStorage', BANNER_DISMISS_KEY, '1');
                setBannerDismissed(true);
              }}
              aria-label="Dismiss"
              className="rounded p-1 text-amber-800 hover:bg-amber-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}

      {popup ? (
        <div className="fixed bottom-4 right-4 z-[1300] w-[calc(100%-2rem)] max-w-sm rounded-xl border border-gray-200 bg-white p-4 shadow-2xl">
          <div className="flex items-start gap-3">
            <Sparkles
              className={`mt-0.5 h-5 w-5 shrink-0 ${popup.step >= 90 ? 'text-red-600' : 'text-amber-600'}`}
            />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">{popup.title}</p>
              {popup.message ? (
                <p className="mt-1 text-xs leading-relaxed text-gray-700">{popup.message}</p>
              ) : null}
              <UsageBar percent={popup.percent} />
              <p className="mt-1 text-[11px] text-gray-500">
                {Number(popup.used || 0).toLocaleString('en-IN')} of{' '}
                {Number(popup.total || 0).toLocaleString('en-IN')} tokens
              </p>
              <div className="mt-3 flex items-center gap-2">
                <OwnerAction canManage={data.canManage} />
                <button
                  type="button"
                  onClick={closePopup}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                  OK
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={closePopup}
              aria-label="Close"
              className="rounded p-1 text-gray-500 hover:bg-gray-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
